import FormField from 'Components/Common/FormField';
import { PRIVILEGE } from 'constants/privilege';
import withAccessControl from 'hoc/withAccessControl';
import { usePrivileges } from 'hooks/useAuth';
import CampaignCreationLayout from 'Layouts/CampaignCreationLayout'
import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Spinner } from 'reactstrap';
import { updateCampaignMilestone } from 'services/api/campaign';
import { RootState, AppDispatch } from 'store';

const CampaignControls = () => {
    const { campaignId } = useParams();

    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);

    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();

    const [loading, setLoading] = React.useState<boolean>(false);
    const privileges = usePrivileges();
    const campaignGigs = useSelector((state: RootState) => state.campaignMilestones.gigs[campaignId!]?.items || []);
    const campaignMilestones = useSelector((state: RootState) => state.campaignMilestones.items[campaignId!]?.milestonesData?.milestones);
    const gigTypes = useSelector((state: RootState) => state.campaignMilestones.items[campaignId!]?.milestonesData?.gig_types);
    const campaignMilestone = (Array.isArray(campaignMilestones) && campaignMilestones.length > 0) ? campaignMilestones[0] : null;
    const properties = useSelector((state: RootState) => state.campaignMilestones.items[campaignId!]?.milestonesData?.properties);
    const [milestone, setMilestone] = useState(campaignMilestone ?? null);
    const milestoneGigType = (gigTypes ?? []).find((gigType) => gigType.id && milestone?.gig_type_id && gigType.id === milestone.gig_type_id);
    const milestoneGigs = (campaignGigs ?? []).filter((gig) => (milestone?.campaign_gig_ids ?? []).includes(gig.id))

    useEffect(() => {
        setMilestone(campaignMilestone ?? null)
    }, [campaignMilestone])

    const handleMilestoneChange = (key: string, value: any) => {
        if (!milestone) return;
        setMilestone({ ...milestone, [key]: value })
    }

    const submitControls = async () => {
        if (!campaignId || !milestone?.id || !selectedAgencyId) return;

        setLoading(true);
        try {
            await updateCampaignMilestone(selectedAgencyId, campaignId, String(milestone.id), milestone);
            navigate('/campaign/' + campaignId + '/payment-terms');
        } catch (error) {
            toast.error('Failed to update milestone');
        } finally {
            setLoading(false);
        }
    }

    return (
        <CampaignCreationLayout
            activeStep='controls'
            actionRow={
                <div className="d-flex justify-content-between mb-4">
                    <div></div>
                    <button type='button' onClick={submitControls} disabled={loading || !privileges.includes(PRIVILEGE.CAMPAIGN.CAMPAIGN.EDIT)} className="theme-btn theme-btn-lg btn-gradient form-continue-btn d-flex justify-content-between align-items-center">
                        <React.Fragment>
                            {
                                'Proceed to Next'
                            }
                            {
                                loading ? <Spinner size="sm" className='me-2'> Loading... </Spinner> : <i className='bx fs-4 bx-right-arrow-alt' ></i>
                            }
                        </React.Fragment>
                    </button>
                </div>
            }
        >
            {
                milestone &&
                <div className='campaign-controls'>
                    {
                        milestoneGigType?.has_gigs &&
                        <>
                            <div className="form-group">
                                <label htmlFor="aitcp" className="fs-14px mb-2">{properties?.gig_influencer?.label} {milestoneGigType?.object_name}</label>
                                <FormField
                                    showLabel={false}
                                    property={{ ...properties?.gig_influencer, label: '' }}
                                    value={!!milestone?.specifications.gig_influencer}
                                    onChange={(value) => {
                                        const specifications = { ...milestone.specifications, gig_influencer: value };
                                        handleMilestoneChange('specifications', specifications);
                                    }}
                                />
                            </div>
                            <div className="divider mt-3 my-4"></div>
                        </>
                    }
                    {
                        !(milestone?.specifications?.quick_repost) &&
                        <>
                            <div className="form-group">
                                <label htmlFor="aitcp" className="fs-14px mb-2">{properties?.cta_influencer?.label}</label>
                                <FormField
                                    showLabel={false}
                                    property={{ ...properties?.cta_influencer, label: '' }}
                                    value={!!milestone?.specifications.cta_influencer}
                                    onChange={(value) => {
                                        const specifications = { ...milestone.specifications, cta_influencer: value };
                                        handleMilestoneChange('specifications', specifications);
                                    }}
                                />
                            </div>
                            <div className="divider mt-3 my-4"></div>
                        </>
                    }
                    {
                        !(milestone?.specifications?.quick_repost) && Object.values(properties ?? {}).sort((a, b) => a.sort_order - b.sort_order).map((property, i) => (property.gig_type_id === milestone.gig_type_id && property.auto_render) ? (
                            <React.Fragment key={i}>
                                {
                                    ((property.key === 'review_snapshot_platform' && !milestone.specifications?.review_snapshot_required) || (['advertiser_permission_required', 'paid_partnership_label_required'].includes(property.key))) ? null : (
                                        <>
                                            <div className="form-group">
                                                <label htmlFor="aitcp" className="fs-14px mb-2">{property?.label} {property.mandatory && <span className="text-danger">*</span>}</label>
                                                <FormField
                                                    showLabel={false}
                                                    property={{ ...property, label: '' }}
                                                    value={property.type === 'boolean' ? !!milestone?.specifications[property.key] : milestone?.specifications[property.key]}
                                                    onChange={(value) => {
                                                        const specifications = { ...milestone.specifications, [property.key]: value };
                                                        handleMilestoneChange('specifications', specifications);
                                                    }}
                                                />
                                            </div>
                                            <div className="divider mt-3 my-4"></div>
                                        </>

                                    )
                                }
                            </React.Fragment>
                        ) : null)
                    }
                    {
                        milestoneGigType?.slug !== 'repost' &&
                        <>
                            <div className="form-group">
                                <label htmlFor="aitcp" className="fs-14px mb-2">{properties?.script_submission?.label}</label>
                                <FormField
                                    showLabel={false}
                                    property={{ ...properties?.script_submission, label: '' }}
                                    value={!!milestone?.specifications.script_submission}
                                    onChange={(value) => {
                                        const specifications = { ...milestone.specifications, script_submission: value };
                                        handleMilestoneChange('specifications', specifications);
                                    }}
                                />
                            </div>
                            <div className="divider mt-3 my-4"></div>
                        </>
                    }
                    {
                        !(milestone?.specifications?.quick_repost) &&
                        <>
                            <div className="form-group">
                                <label htmlFor="aitcp" className="fs-14px mb-2">{properties?.caption_submission?.label}</label>
                                <FormField
                                    showLabel={false}
                                    property={{ ...properties?.caption_submission, label: '' }}
                                    value={!!milestone?.specifications.caption_submission}
                                    onChange={(value) => {
                                        const specifications = { ...milestone.specifications, caption_submission: value };
                                        handleMilestoneChange('specifications', specifications);
                                    }}
                                />
                            </div>
                            <div className="divider mt-3 my-4"></div></>
                    }

                </div>
            }

        </CampaignCreationLayout>
    )
}

export default withAccessControl(CampaignControls, PRIVILEGE.CAMPAIGN.CAMPAIGN.EDIT)
